import { useCallback, useRef } from "react";
import { MenuItem } from "@progress/kendo-react-layout";
import { GridCellProps } from "@progress/kendo-react-grid";
import { useSelector } from "react-redux";
import { RootState } from "../../app";
import { router, routes } from "../../app/routes";
import { UserInviteModel } from "../../data/entities";
import { InviteRequest } from "../../models";
import { PermissionDefinitions } from "../../security";
import { UserInviteService } from "../../services";
import { showErrorMessage, showSuccessMessage } from "../../utils";
import { useGridActions, usePermission, useSetPageTitle } from "../../hooks";
import { useExpiryDate } from "./hooks";
import { usePageActions } from "../";
import { AddUserInviteModal } from "./components";
import {
  Breakpoints,
  Button,
  ConfirmationModal,
  Icon,
  IconAndText,
  IconDefinitions,
  ODataGrid,
  ResponsiveGridColumn,
  TitlelessCard,
} from "../../components";

export function UserInvitationsPage() {

  const userInviteService = new UserInviteService();
  const { onClick, onSelect, showDelete, showAddState, showDeleteState, deleteItemState } = useGridActions<UserInviteModel>(routes.user.invite);
  const gridRef = useRef<ODataGrid>(null);
  const { getExpiryDateCell } = useExpiryDate();

  const tenantId = useSelector((state: RootState) => state.authentication.tenantId);
  const canAddUser = usePermission(PermissionDefinitions.user.create(tenantId!));
  const canDeleteUser = usePermission(PermissionDefinitions.user.delete(tenantId!));

  useSetPageTitle("Invitations", []);

  const addInviteHandler = async (request: InviteRequest) => {
    try {
      const ok = await userInviteService.insert(request);
      if (ok) {
        showSuccessMessage(`Invitation sent.`);
        showAddState?.setter(false);
      }
    } catch (error) {
      showErrorMessage(`Error occurred while trying to execute insert: ${error}`);
    } finally {
      gridRef.current?.refreshData();
    }
  };

  const deleteInviteHandler = async (item: UserInviteModel) => {
    try {
      const ok = await userInviteService.delete(item.id);
      if (ok) {
        showSuccessMessage(`Invitation deleted.`);
        showDeleteState?.setter(false);
      }
    } catch (error) {
      showErrorMessage(`Error occurred while trying to execute delete: ${error}`);
    } finally {
      gridRef.current?.refreshData();
    }
  }

  usePageActions(
    canAddUser && (
      <Button
        onClick={() => {
          showAddState.setter(true);
        }}
      >
        <IconAndText iconName={IconDefinitions.add} text="Invite" />
      </Button>
    ),
    [canAddUser]
  );

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <>
        <tr>
          {canAddUser && (
            <td>
              <Icon
                iconName={IconDefinitions.externalLink}
                className="ml-2 text-primary"
                onClick={() => { router.navigateWithParams(routes.account.register.index, new Map([["id", props.dataItem.id], ["key", props.dataItem.key]]), true); }}
              />
            </td>
          )}
          { canDeleteUser && (
            <td className="text-center">
              <Icon
                iconName={IconDefinitions.delete}
                className="text-danger"
                onClick={() => showDelete(props.dataItem)}
              />
            </td>
          )}
        </tr>
      </>
    ),
    [showDelete, canDeleteUser]
  );

  return (
    <TitlelessCard className="p-0 m-0" bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => userInviteService.query(`${oDataQuery}`)}
        onRowMenuSelect={onSelect}
        onRowClick={onClick}
        sort={[{ field: "expiryDate", dir: "desc" }]}
        scrollable="scrollable"
      >
        <ResponsiveGridColumn
          title="Email"
          field="email"
          sortable={true}
        />
        <ResponsiveGridColumn
          title="Expires"
          field="expiryDate"
          sortable={true}
          filterable={false}
          cell={getExpiryDateCell}
        />
        <ResponsiveGridColumn
          title="Used"
          field="isUsed"
          sortable={true}
          filterable={false}
          width={100}
          cell={(props) => <td>{props.dataItem.isUsed ? "Yes" : "No"}</td>}
        />
        {canDeleteUser && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={canDeleteUser === true ? commandsCell : undefined}
            width={80}
            breakpoint={Breakpoints.sm}
          />)}
        {canDeleteUser && (
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        )}
      </ODataGrid>
      <AddUserInviteModal
        showAddState={showAddState}
        showState={showAddState}
        onSubmit={addInviteHandler}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.email}`}
        message={`You are about to delete this invitation, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.email}?`}
        confirmText="Delete"
        onConfirm={() => deleteInviteHandler(deleteItemState.value)}
      />
    </TitlelessCard>
  );
}

export default UserInvitationsPage;

